/**
 * R-PAT Pre-Training Onboarding
 * Interactive onboarding experience for first-time recruitment training users
 * Flow: Welcome → Background → Goals → World of Recruitment → Types → Ready
 */

import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
    GraduationCap, Briefcase, Target, TrendingUp, Users, Clock, Award,
    ChevronRight, ChevronLeft, CheckCircle2, BookOpen, Mic, Building2,
    Handshake, UserCheck, Search, FileText, Globe, DollarSign, 
    HeartHandshake, Layers, Zap, Shield, Star
} from 'lucide-react';

const ONBOARDING_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to Recruitment Training',
        subtitle: 'Your journey to becoming a skilled recruiter begins here'
    },
    {
        id: 'background',
        title: 'Your Background',
        subtitle: 'Tell us a bit about yourself'
    },
    {
        id: 'goals',
        title: 'Your Learning Goals',
        subtitle: 'What do you want to achieve?'
    },
    {
        id: 'world',
        title: 'The World of Recruitment',
        subtitle: 'Understanding the recruitment landscape'
    },
    {
        id: 'types',
        title: 'Types of Recruitment',
        subtitle: 'Different paths in the recruitment industry'
    },
    {
        id: 'overview',
        title: 'Training Overview',
        subtitle: '8 comprehensive phases to certification'
    },
    {
        id: 'ready',
        title: 'Ready to Begin',
        subtitle: "Let's set you up for success"
    }
];

// How did they land here?
const BACKGROUND_OPTIONS = [
    { id: 'new_to_recruitment', label: "I'm new to recruitment", icon: Star, description: 'Just starting my journey' },
    { id: 'some_experience', label: 'I have some recruitment experience', icon: TrendingUp, description: '1-2 years in the field' },
    { id: 'seasoned_recruiter', label: "I'm a seasoned recruiter looking to level up", icon: Award, description: 'Want advanced techniques' },
    { id: 'company_enrolled', label: 'My company enrolled me in this training', icon: Building2, description: 'Part of team development' }
];

// What are they trying to achieve?
const GOAL_OPTIONS = [
    { id: 'learn_fundamentals', label: 'Learn recruitment fundamentals', icon: BookOpen },
    { id: 'improve_sourcing', label: 'Improve candidate sourcing skills', icon: Search },
    { id: 'master_client_management', label: 'Master client relationship management', icon: Handshake },
    { id: 'get_certified', label: 'Get certified in staffing best practices', icon: Award },
    { id: 'increase_placements', label: 'Increase my placement rate', icon: Target },
    { id: 'learn_interviewing', label: 'Learn effective interviewing techniques', icon: Mic }
];

// World of Recruitment content
const RECRUITMENT_WORLD = [
    {
        icon: Globe,
        title: '$500B+ Global Industry',
        description: 'The staffing industry is a massive global market connecting talent with opportunities worldwide.'
    },
    {
        icon: Users,
        title: 'Connecting People',
        description: 'At its core, recruitment is about matching the right people with the right opportunities.'
    },
    {
        icon: Building2,
        title: 'Diverse Clients',
        description: 'From startups to Fortune 500 companies, recruiters work with organizations of all sizes.'
    },
    {
        icon: DollarSign,
        title: 'High-Value Profession',
        description: 'Skilled recruiters are among the highest-paid professionals in the HR industry.'
    }
];

// Types of Recruitment
const RECRUITMENT_TYPES = [
    {
        title: 'Staffing Agency Recruitment',
        description: 'Work with multiple clients, place candidates across various companies',
        icon: Building2,
        subtypes: ['Contract Staffing', 'Temp-to-Hire', 'Direct Placement'],
        color: 'from-blue-500 to-cyan-600'
    },
    {
        title: 'Corporate/In-House Recruitment',
        description: 'Recruit exclusively for one organization, deep company knowledge',
        icon: Briefcase,
        subtypes: ['Internal HR', 'Talent Acquisition', 'Employer Branding'],
        color: 'from-purple-500 to-violet-600'
    },
    {
        title: 'Contract vs Permanent',
        description: 'Different hiring models for different business needs',
        icon: FileText,
        subtypes: ['Contract (W2/1099)', 'Contract-to-Hire', 'Full-Time Permanent'],
        color: 'from-amber-500 to-orange-600'
    },
    {
        title: 'Specialized Recruitment',
        description: 'Focus on specific industries or job functions',
        icon: Target,
        subtypes: ['IT/Tech', 'Healthcare', 'Finance', 'Executive Search'],
        color: 'from-emerald-500 to-teal-600'
    }
];

// Training phases
const TRAINING_PHASES = [
    {
        phase: 1,
        name: 'Foundation',
        description: 'Master staffing basics, industry terminology, and core concepts',
        topics: 6,
        color: 'from-cyan-500 to-blue-600'
    },
    {
        phase: 2,
        name: 'Core Skills',
        description: 'Develop essential skills: sourcing, screening, and candidate management',
        topics: 6,
        color: 'from-violet-500 to-purple-600'
    },
    {
        phase: 3,
        name: 'Communication & Interview',
        description: 'Perfect your communication, interviewing, and client interaction skills',
        topics: 6,
        color: 'from-amber-500 to-orange-600'
    },
    {
        phase: 4,
        name: 'Practical Application',
        description: 'Apply everything through simulations, role-plays, and real scenarios',
        topics: 5,
        color: 'from-emerald-500 to-teal-600'
    }
];

const RPATOnboarding = ({ onComplete, userName }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedBackground, setSelectedBackground] = useState(null);
    const [selectedGoals, setSelectedGoals] = useState([]);

    const step = ONBOARDING_STEPS[currentStep];
    const progress = ((currentStep + 1) / ONBOARDING_STEPS.length) * 100;

    const canProceed = () => {
        if (step.id === 'background') return selectedBackground !== null;
        if (step.id === 'goals') return selectedGoals.length > 0;
        return true;
    };

    const handleNext = () => {
        if (currentStep < ONBOARDING_STEPS.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            onComplete({ selectedBackground, selectedGoals });
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const toggleGoal = (id) => {
        if (selectedGoals.includes(id)) {
            setSelectedGoals(selectedGoals.filter(item => item !== id));
        } else {
            setSelectedGoals([...selectedGoals, id]);
        }
    };

    return (
        <div className="min-h-screen bg-[hsl(240,10%,4%)] text-white flex flex-col" data-testid="rpat-onboarding">
            {/* Progress Bar */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-4xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-mono tracking-widest text-white/60 uppercase">
                            Step {currentStep + 1} of {ONBOARDING_STEPS.length}
                        </span>
                        <span className="text-xs font-mono tracking-widest text-white/60">
                            {step.title}
                        </span>
                    </div>
                    <Progress value={progress} className="h-1 bg-white/10" />
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center pt-20 pb-32 px-6">
                <div className="max-w-4xl w-full">
                    
                    {/* Step: Welcome */}
                    {step.id === 'welcome' && (
                        <div className="text-center space-y-8 animate-in fade-in duration-500">
                            <div className="inline-flex p-6 rounded-3xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30">
                                <GraduationCap className="w-16 h-16 text-cyan-400" />
                            </div>
                            
                            <div className="space-y-4">
                                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter">
                                    Welcome{userName ? `, ${userName}` : ' to R-PAT'}
                                </h1>
                                <p className="text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
                                    You&apos;re about to embark on a comprehensive journey to master the art and science of recruitment. 
                                    This training will transform you into a confident, skilled recruiter.
                                </p>
                            </div>

                            <div className="grid grid-cols-3 gap-4 max-w-xl mx-auto pt-4">
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <BookOpen className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                                    <p className="text-2xl font-bold">23</p>
                                    <p className="text-xs text-white/60">Topics</p>
                                </div>
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <Layers className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                                    <p className="text-2xl font-bold">8</p>
                                    <p className="text-xs text-white/60">Phases</p>
                                </div>
                                <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                                    <Award className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                                    <p className="text-2xl font-bold">1</p>
                                    <p className="text-xs text-white/60">Certification</p>
                                </div>
                            </div>

                            <div className="flex items-center justify-center gap-3 pt-4 text-sm text-white/60">
                                <Mic className="w-4 h-4" />
                                <span>Voice-Based Interactive Training</span>
                            </div>
                        </div>
                    )}

                    {/* Step: Background */}
                    {step.id === 'background' && (
                        <div className="space-y-8 animate-in fade-in duration-500">
                            <div className="text-center space-y-4">
                                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                                    How Did You Land Here?
                                </h1>
                                <p className="text-white/70 max-w-xl mx-auto">
                                    Understanding your background helps us personalize your learning experience.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {BACKGROUND_OPTIONS.map(option => {
                                    const Icon = option.icon;
                                    const isSelected = selectedBackground === option.id;
                                    return (
                                        <button
                                            key={option.id}
                                            onClick={() => setSelectedBackground(option.id)}
                                            className={`p-6 rounded-2xl border text-left transition-all duration-200 relative ${
                                                isSelected 
                                                    ? 'bg-cyan-500/20 border-cyan-500/50 ring-2 ring-cyan-500/30'
                                                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                                            }`}
                                            data-testid={`background-option-${option.id}`}
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className={`p-3 rounded-xl ${isSelected ? 'bg-cyan-500/30' : 'bg-white/10'}`}>
                                                    <Icon className={`w-6 h-6 ${isSelected ? 'text-cyan-400' : 'text-white/60'}`} />
                                                </div>
                                                <div className="flex-1">
                                                    <p className={`font-medium text-lg ${isSelected ? 'text-white' : 'text-white/80'}`}>
                                                        {option.label}
                                                    </p>
                                                    <p className="text-sm text-white/50 mt-1">{option.description}</p>
                                                </div>
                                                {isSelected && (
                                                    <CheckCircle2 className="w-6 h-6 text-cyan-400 flex-shrink-0" />
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Step: Goals */}
                    {step.id === 'goals' && (
                        <div className="space-y-8 animate-in fade-in duration-500">
                            <div className="text-center space-y-4">
                                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                                    What Are You Trying to Achieve?
                                </h1>
                                <p className="text-white/70 max-w-xl mx-auto">
                                    Select your top learning goals. We&apos;ll track your progress toward these throughout the program.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {GOAL_OPTIONS.map(option => {
                                    const Icon = option.icon;
                                    const isSelected = selectedGoals.includes(option.id);
                                    return (
                                        <button
                                            key={option.id}
                                            onClick={() => toggleGoal(option.id)}
                                            className={`p-6 rounded-2xl border text-left transition-all duration-200 relative ${
                                                isSelected 
                                                    ? 'bg-emerald-500/20 border-emerald-500/50 ring-2 ring-emerald-500/30'
                                                    : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                                            }`}
                                            data-testid={`goal-option-${option.id}`}
                                        >
                                            <Icon className={`w-6 h-6 mb-3 ${isSelected ? 'text-emerald-400' : 'text-white/60'}`} />
                                            <p className={`font-medium ${isSelected ? 'text-white' : 'text-white/80'}`}>
                                                {option.label}
                                            </p>
                                            {isSelected && (
                                                <CheckCircle2 className="w-5 h-5 text-emerald-400 absolute top-3 right-3" />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Step: World of Recruitment */}
                    {step.id === 'world' && (
                        <div className="space-y-8 animate-in fade-in duration-500">
                            <div className="text-center space-y-4">
                                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                                    The World of Recruitment
                                </h1>
                                <p className="text-white/70 max-w-xl mx-auto">
                                    Recruitment is more than just filling positions—it&apos;s about building teams that drive success.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {RECRUITMENT_WORLD.map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <div 
                                            key={`world-item-${index}`}
                                            className="p-6 rounded-2xl bg-white/5 border border-white/10 text-center"
                                        >
                                            <div className="inline-flex p-3 rounded-xl bg-cyan-500/20 mb-4">
                                                <Icon className="w-6 h-6 text-cyan-400" />
                                            </div>
                                            <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                                            <p className="text-sm text-white/60">{item.description}</p>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="p-6 rounded-2xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
                                <p className="text-center text-lg text-white/80">
                                    <span className="text-cyan-400 font-semibold">Did you know?</span> The average recruiter makes 
                                    <span className="text-white font-bold"> 52 calls</span>, sends 
                                    <span className="text-white font-bold"> 36 emails</span>, and conducts 
                                    <span className="text-white font-bold"> 8 interviews</span> to make a single placement.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Step: Types of Recruitment */}
                    {step.id === 'types' && (
                        <div className="space-y-8 animate-in fade-in duration-500">
                            <div className="text-center space-y-4">
                                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                                    Types of Recruitment
                                </h1>
                                <p className="text-white/70 max-w-xl mx-auto">
                                    The recruitment industry offers diverse career paths. Here are the main types you&apos;ll learn about.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {RECRUITMENT_TYPES.map((type, index) => {
                                    const Icon = type.icon;
                                    return (
                                        <div 
                                            key={`type-${index}`}
                                            className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className={`p-3 rounded-xl bg-gradient-to-br ${type.color} flex-shrink-0`}>
                                                    <Icon className="w-6 h-6 text-white" />
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="font-bold text-lg mb-1">{type.title}</h3>
                                                    <p className="text-sm text-white/60 mb-3">{type.description}</p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {type.subtypes.map((subtype, i) => (
                                                            <Badge 
                                                                key={`subtype-${index}-${i}`}
                                                                variant="secondary" 
                                                                className="bg-white/10 text-white/70 text-xs"
                                                            >
                                                                {subtype}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Step: Training Overview */}
                    {step.id === 'overview' && (
                        <div className="space-y-8 animate-in fade-in duration-500">
                            <div className="text-center space-y-4">
                                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
                                    Your Learning Path
                                </h1>
                                <p className="text-white/70 max-w-xl mx-auto">
                                    The R-PAT program is structured into 8 phases - 4 foundation phases followed by 4 advanced certification phases. 
                                    Complete all phases to earn your TekWissen Certified Recruiter certification.
                                </p>
                            </div>

                            <div className="grid gap-4">
                                {TRAINING_PHASES.map((phase, index) => (
                                    <div 
                                        key={`phase-${phase.phase}`}
                                        className="flex items-center gap-6 p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                                    >
                                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${phase.color} flex items-center justify-center flex-shrink-0`}>
                                            <span className="text-2xl font-bold text-white">{phase.phase}</span>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-1">
                                                <h3 className="font-bold text-lg">{phase.name}</h3>
                                                <Badge variant="secondary" className="bg-white/10 text-white/70">
                                                    {phase.topics} topics
                                                </Badge>
                                            </div>
                                            <p className="text-white/60">{phase.description}</p>
                                        </div>
                                        {index === 0 && (
                                            <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                                                Starts here
                                            </Badge>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step: Ready */}
                    {step.id === 'ready' && (
                        <div className="text-center space-y-8 animate-in fade-in duration-500">
                            <div className="inline-flex p-6 rounded-full bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30">
                                <CheckCircle2 className="w-16 h-16 text-emerald-400" />
                            </div>
                            
                            <div className="space-y-4">
                                <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter">
                                    You&apos;re All Set!
                                </h1>
                                <p className="text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
                                    Before you begin, here are a few things to know about the training experience:
                                </p>
                            </div>

                            <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
                                <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-left">
                                    <Mic className="w-8 h-8 text-cyan-400 mb-4" />
                                    <h3 className="font-bold mb-2">Voice-Based Learning</h3>
                                    <p className="text-sm text-white/60">
                                        Listen to lessons, practice speaking, and get real-time feedback on your delivery.
                                    </p>
                                </div>
                                <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-left">
                                    <BookOpen className="w-8 h-8 text-purple-400 mb-4" />
                                    <h3 className="font-bold mb-2">Progress Saved</h3>
                                    <p className="text-sm text-white/60">
                                        Your progress is automatically saved. Pick up right where you left off anytime.
                                    </p>
                                </div>
                                <div className="p-6 rounded-2xl bg-white/5 border border-white/10 text-left">
                                    <Target className="w-8 h-8 text-emerald-400 mb-4" />
                                    <h3 className="font-bold mb-2">Quizzes & Assessments</h3>
                                    <p className="text-sm text-white/60">
                                        Test your knowledge with quizzes after each topic to reinforce your learning.
                                    </p>
                                </div>
                            </div>

                            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 max-w-md mx-auto">
                                <p className="text-sm text-amber-200">
                                    <strong>Tip:</strong> Use headphones and find a quiet space for the best learning experience.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation Footer */}
            <div className="fixed bottom-0 left-0 right-0 bg-black/80 backdrop-blur-xl border-t border-white/10">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Button
                        variant="ghost"
                        onClick={handleBack}
                        disabled={currentStep === 0}
                        className="text-white/70 hover:text-white hover:bg-white/10"
                        data-testid="onboarding-back-btn"
                    >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>

                    <div className="flex gap-2">
                        {ONBOARDING_STEPS.map((_, index) => (
                            <div 
                                key={`step-dot-${index}`}
                                className={`w-2 h-2 rounded-full transition-colors ${
                                    index === currentStep 
                                        ? 'bg-cyan-500' 
                                        : index < currentStep 
                                        ? 'bg-emerald-500' 
                                        : 'bg-white/20'
                                }`}
                            />
                        ))}
                    </div>

                    <Button
                        onClick={handleNext}
                        disabled={!canProceed()}
                        className="bg-cyan-600 hover:bg-cyan-700 text-white px-8"
                        data-testid="onboarding-next-btn"
                    >
                        {currentStep === ONBOARDING_STEPS.length - 1 ? 'Start Training' : 'Continue'}
                        <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default RPATOnboarding;
